/**
 * Sticky banner shown app-wide whenever a staff member is impersonating a
 * chef in read-only mode.  Provides an "Exit" button that:
 *   1. POSTs /api/admin/impersonation/exit using the original staff JWT
 *   2. Clears the impersonation token from sessionStorage
 *   3. Navigates back to HQ Command Users
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Eye, LogOut, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { ImpersonationStore } from "@/lib/impersonation";

export default function ImpersonationBanner() {
  const navigate = useNavigate();
  const [imp, setImp] = useState(() => ImpersonationStore.read());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const sync = () => setImp(ImpersonationStore.read());
    window.addEventListener("pp-impersonation-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("pp-impersonation-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (!imp) return null;

  const exit = async () => {
    setBusy(true);
    const { data: { session } } = await supabase.auth.getSession();
    try {
      await axios.post(
        `${API}/admin/impersonation/exit`,
        {
          ticket_id: imp.ticket_id,
          chef_id: imp.chef_id,
          duration_seconds: Math.max(0, Math.floor(Date.now() / 1000 - (imp.issued_at || imp.expires_at - 3600))),
        },
        { headers: { Authorization: `Bearer ${session?.access_token || ""}` } }
      );
    } catch {
      /* still clear locally even if audit log fails */
    } finally {
      ImpersonationStore.clear();
      setBusy(false);
      navigate(`/hq-command-77/users?tab=customer-voice&t=${imp.ticket_id}`);
    }
  };

  const expiresIn = Math.max(0, Math.floor((imp.expires_at * 1000 - Date.now()) / 60000));

  return (
    <div
      className="sticky top-0 z-50 w-full bg-gradient-to-r from-rose-600 via-rose-500 to-amber-500 text-white px-4 py-2 shadow-md"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <Eye className="h-4 w-4 flex-none" />
          <span className="uppercase tracking-wide">Read-only mode</span>
          <span className="opacity-80 normal-case font-normal">
            · Impersonating
            <span className="font-bold ml-1 font-mono" data-testid="impersonation-chef-email">
              {imp.chef_email || imp.chef_id?.slice(0, 8) || "chef"}
            </span>
            {imp.ticket_reference && (
              <span className="opacity-70 ml-2 text-[0.7rem] font-mono">
                ({imp.ticket_reference})
              </span>
            )}
          </span>
          <span className="opacity-60 normal-case font-normal text-[0.7rem] ml-2">
            · {expiresIn}m left
          </span>
        </div>
        <button
          type="button"
          onClick={exit}
          disabled={busy}
          className="inline-flex items-center gap-1.5 bg-slate-900/70 hover:bg-slate-950 px-3 py-1 rounded font-semibold text-xs transition-colors disabled:opacity-50"
          data-testid="impersonation-exit-btn"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
          Exit Impersonation
        </button>
      </div>
    </div>
  );
}
